import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CargoheatingCommonService } from 'src/app/shared/service/cargo-heating-service/cargoheating-common.service';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';
import { filter, Subscription } from 'rxjs';

const OUTPUT_CALC_COL_PREFIX = '__outcalc__';
const OUTPUT_CALC_TOTAL_CONSUMPTION = `${OUTPUT_CALC_COL_PREFIX}total_consumption`;
const OUTPUT_CALC_CONSUMPTION_MAINTAIN = `${OUTPUT_CALC_COL_PREFIX}consumption_maintain`;
const OUTPUT_CALC_CONSUMPTION_RAISE = `${OUTPUT_CALC_COL_PREFIX}consumption_raise`;

/** Fixed mappings for the three static day-wise consumption columns. */
const TOTAL_CONSUMPTION_FACTOR_KEYS = [
  'bunker_m',
  'bunker_r',
  'ae',
  'dhe',
  'pipeline_loss',
] as const;
const CONSUMPTION_MAINTAIN_FACTOR_KEYS = [
  'bunker_m',
  'ae',
  'dhe',
  'pipeline_loss',
] as const;
const CONSUMPTION_RAISE_FACTOR_KEYS = ['bunker_r'] as const;

/** Weather columns frozen beside S.No / Date, in display order (matched case-insensitively). */
const FROZEN_WEATHER_COLUMN_KEYS = ['air temp', 'water temp', 'wind speed'];

@Component({
  selector: 'app-forecast-temperatures-drop',
  templateUrl: './forecast-temperatures-drop.component.html',
  styleUrls: ['./forecast-temperatures-drop.component.scss']
})
export class ForecastTemperaturesDropComponent implements OnInit, OnDestroy {

  columns: string[] = [];
  allColumns: { name: string, label: string, visible: boolean }[] = [];
  rowData: any[] = [];
  originalRowData: any[] = [];   // snapshot for discard
  indexes: number[] = [];
  /** Cells with unsaved user edits (drives Calibrate button). */
  editedCells: { [key: string]: boolean } = {};
  /** Forecast temp cells manually adjusted by the user (value-only red highlight). */
  manuallyAdjustedCells: { [key: string]: boolean } = {};
  hasEdits: boolean = false;
  private editHistory: Array<{ rowIndex: number; column: string; previousValue: number | string }> = [];

  /** Tracks which cell is currently in inline-edit mode: "rowIndex_colName" */
  activeEditCell: string | null = null;
  /** Live text of the open cell editor (drives the Δ-vs-previous-day chip). */
  editDraft = '';
  /** Cell that just received a committed change — plays a short flash. */
  flashCellKey: string | null = null;
  private flashTimer?: ReturnType<typeof setTimeout>;
  private tableEl: HTMLElement | null = null;

  imo: number = 0;
  isCollapsedView: boolean = true;
  /** Weather columns (Air Temp / Water Temp / Wind Speed) are hidden by default to focus on temperature drops. */
  showWeatherColumns: boolean = false;
  /** Expand/Collapse control is visible only to Super Admin (Meraki Operations). */
  isSuperAdmin: boolean = false;
  isLoading: boolean = false;
  heatPlanResponse: any = null;
  isSubmitted: boolean = false;
  isSubmitting: boolean = false;

  showColumnChooser: boolean = false;
  vesselName: string = '';
  vesselIMO: string = '';
  heatplanNo: string = '';
  subusername: any;

  get formattedHeatplanNo(): string {
    if (!this.heatplanNo) {
      return '';
    }
    const val = this.heatplanNo.trim();
    if (/^\d+$/.test(val)) {
      return `Heat Plan ${val}`;
    }
    return val;
  }

  /** Persisted alters array — accumulates across calibrate calls */
  persistedAlters: { day: string;[tank: string]: number | string }[] = [];

  voyMin: any = {};
  voyMax: any = {};
  voyMinMeraki: any = {};
  voyMaxMeraki: any = {};
  dischMin: any = {};
  dischMax: any = {};
  cargoQty: any = {};
  cargo_contents: any = {};
  cargoType: any = {};
  loadTemp: any = {};
  pourPoint: any = {};
  loadPorts: any = {};
  dischargePorts: any = {};

  /** Multi-select Cargo Sub Type filter (empty / all = no cargo filter). */
  selectedCargoSubTypes: string[] = [];
  cargoSubTypeOptions: string[] = [];

  /** Multi-select Tank filter (empty / all = no tank filter; options respect cargo filter). */
  selectedTanks: string[] = [];
  tankFilterOptions: string[] = [];

  /** Calibration loading indicator */
  isCalibrating = false;

  /** Tolerance filter settings for temperature increase highlight */
  selectedTolerancePreset: number | 'custom' = 0.5;
  customToleranceValue: number = 0.5;

  private loadedHeatPlanVersion = -1;
  private routerSubscription?: Subscription;
  /** Cache: row + column + filter scope → day-wise sum */
  private outputCalcSumCache = new Map<string, number | null>();

  hoverSummary = {
    active: false,
    currentTankKey: '',
    cargoType: '',
    cargoSubType: '',
    cargoQty: null as number | null,
    voyMin: null as number | null,
    voyMax: null as number | null,
    voyMinMeraki: null as number | null,
    voyMaxMeraki: null as number | null,
    dischMin: null as number | null,
    dischMax: null as number | null,
    loadTemp: null as number | null,
    pourPoint: null as number | null,
    loadPort: '',
    dischargePort: '',
  };

  /**
   * Tank shown in the particulars bar when the cursor is not over a tank column.
   * Defaults to the first visible tank; clicking a tank header pins that tank instead.
   */
  pinnedTankKey = '';
  /** True while the bar shows a hovered tank rather than the pinned one. */
  isTankPreview = false;

  /** column name → normalised tank key (null for non-tank columns); columns are stable per load. */
  private columnTankKeyCache = new Map<string, string | null>();
  private removeTableHoverListeners?: () => void;

  /**
   * One delegated hover listener for the whole table, registered outside Angular so moving
   * across cells does not run change detection. It re-enters the zone only when the hovered
   * tank actually changes.
   */
  @ViewChild('forecastTableContainer') set forecastTableContainer(ref: ElementRef<HTMLElement> | undefined) {
    this.removeTableHoverListeners?.();
    this.removeTableHoverListeners = undefined;
    this.tableEl = ref?.nativeElement ?? null;
    if (!ref) {
      return;
    }

    const el = ref.nativeElement;
    this.ngZone.runOutsideAngular(() => {
      const onOver = (event: MouseEvent) => this.handleTableHover(event);
      const onLeave = () => {
        if (this.isTankPreview) {
          this.ngZone.run(() => this.restorePinnedTank());
        }
      };
      el.addEventListener('mouseover', onOver);
      el.addEventListener('mouseleave', onLeave);
      this.removeTableHoverListeners = () => {
        el.removeEventListener('mouseover', onOver);
        el.removeEventListener('mouseleave', onLeave);
      };
    });
  }

  constructor(
    private http: HttpClient,
    private commonService: CargoheatingCommonService,
    private router: Router,
    private toastr: ToastrService,
    private requestFormService: RequestFormService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) { }

  ngOnInit(): void {
    this.subusername = localStorage.getItem('loginPic') ?? '';
    this.isSuperAdmin = localStorage.getItem('loginPic') === 'superadmin';
    this.loadForecastTable();

    this.routerSubscription = this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        filter((event) => event.urlAfterRedirects.includes('/ch-forecastTemp')),
      )
      .subscribe(() => this.loadForecastTable());
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.removeTableHoverListeners?.();
    clearTimeout(this.flashTimer);
  }

  private resetTableState(): void {
    this.columns = [];
    this.allColumns = [];
    this.rowData = [];
    this.originalRowData = [];
    this.indexes = [];
    this.editedCells = {};
    this.manuallyAdjustedCells = {};
    this.hasEdits = false;
    this.editHistory = [];
    this.activeEditCell = null;
    this.persistedAlters = [];
    this.isSubmitted = false;
    this.isSubmitting = false;
    this.heatPlanResponse = null;
    this.vesselName = '';
    this.vesselIMO = '';
    this.heatplanNo = '';
    this.cargoQty = {};
    this.cargo_contents = {};
    this.voyMin = {};
    this.voyMax = {};
    this.voyMinMeraki = {};
    this.voyMaxMeraki = {};
    this.dischMin = {};
    this.dischMax = {};
    this.cargoType = {};
    this.loadTemp = {};
    this.pourPoint = {};
    this.loadPorts = {};
    this.dischargePorts = {};
    this.selectedCargoSubTypes = [];
    this.cargoSubTypeOptions = [];
    this.selectedTanks = [];
    this.tankFilterOptions = [];
    this.outputCalcSumCache.clear();
    this.columnTankKeyCache.clear();
    this.pinnedTankKey = '';
    this.isTankPreview = false;
    this.hoverSummary = {
      active: false,
      currentTankKey: '',
      cargoType: '',
      cargoSubType: '',
      cargoQty: null,
      voyMin: null,
      voyMax: null,
      voyMinMeraki: null,
      voyMaxMeraki: null,
      dischMin: null,
      dischMax: null,
      loadTemp: null,
      pourPoint: null,
      loadPort: '',
      dischargePort: '',
    };
  }

  private loadForecastTable(): void {
    const dataVersion = this.commonService.getHeatPlanDataVersion();
    if (dataVersion === this.loadedHeatPlanVersion) {
      return;
    }
    this.loadedHeatPlanVersion = dataVersion;
    this.isLoading = true;

    this.resetTableState();

    const rawFromService = this.commonService.getHeatPlanData();
    const shouldRestoreDraft = this.commonService.consumeForecastDraftRestoreFlag();
    const rawResponse = shouldRestoreDraft
      ? this.restoreForecastDraftIfAvailable(rawFromService || {})
      : (rawFromService || {});

    if (rawResponse !== rawFromService) {
      this.commonService.setHeatPlanData(rawResponse);
      this.loadedHeatPlanVersion = this.commonService.getHeatPlanDataVersion();
    }

    this.heatPlanResponse = rawResponse || {};
    if (this.subusername === 'demo') {
      this.vesselName = this.heatPlanResponse?.imo === '9312133' ? 'Vessel 1' : 'Vessel 2';
      this.vesselIMO = 'xxxxxxx'
    } else {
      this.vesselName = this.heatPlanResponse?.vessel_name || '';
      this.vesselIMO = this.heatPlanResponse?.imo || '';
    }

    const rawHeatplanNo =
      this.heatPlanResponse?.heatplan_no ??
      this.heatPlanResponse?.hp_number ??
      this.heatPlanResponse?.hp_np ??
      this.heatPlanResponse?.heat_plan_no ??
      this.heatPlanResponse?.heatplan_number ??
      '';
    this.heatplanNo = rawHeatplanNo ? String(rawHeatplanNo).trim() : '';

    let responseObj: any = null;

    try {
      if (this.heatPlanResponse?.response) {
        responseObj = typeof this.heatPlanResponse.response === 'string'
          ? JSON.parse(this.heatPlanResponse.response)
          : this.heatPlanResponse.response;
      }
    } catch {
      responseObj = null;
    }

    if (responseObj?.columns && responseObj?.data) {
      this.columns = responseObj.columns || [];
      this.indexes = responseObj.index || [];
      this.rowData = this.processData(responseObj);
      this.originalRowData = this.deepCloneRows(this.rowData);

      this.allColumns = this.columns.map(c => ({
        name: c,
        label: this.getColumnLabel(c),
        visible: true
      }));

      this.buildMappingsFromResponse(this.heatPlanResponse);
      this.rebuildCargoSubTypeOptions();
      this.rebuildTankFilterOptions();
      this.seedPersistedAlters(this.heatPlanResponse);
      this.restoreForecastDraftMeta();
      this.seedManuallyAdjustedFromAlters();
      this.updateVisibleColumns();
      this.isLoading = false;
      this.cdr.markForCheck();
    } else {
      this.loadFallbackData();
    }
  }

  /** Deep-clone row array to allow discard */
  private deepCloneRows(rows: any[]): any[] {
    return rows.map(r => ({ ...r }));
  }

  seedPersistedAlters(res: any): void {
    if (Array.isArray(res?.alters) && res.alters.length > 0) {
      this.persistedAlters = [...res.alters];
    }
  }

  /** Restore red highlight for temps previously saved via alters/calibrate. */
  private seedManuallyAdjustedFromAlters(): void {
    for (const entry of this.persistedAlters) {
      const day = entry?.day;
      if (!day) continue;

      const rowIndex = this.indexes.findIndex((ts) => this.formatDate(ts) === day);
      if (rowIndex < 0) continue;

      Object.keys(entry).forEach((tankKey) => {
        if (tankKey === 'day') return;
        const column = this.getColumnFromTankKey(tankKey);
        if (column) {
          this.manuallyAdjustedCells[`${rowIndex}_${column}`] = true;
        }
      });
    }
  }

  private getColumnFromTankKey(tankKey: string): string | null {
    const normalized = String(tankKey).toUpperCase();
    return (
      this.columns.find((col) => {
        const match = this.getTankKeyFromColumn(col);
        return match?.toUpperCase() === normalized && this.isTempColumn(col);
      }) || null
    );
  }

  buildMappingsFromResponse(res: any) {
    this.cargoQty = this.normalizeMapping(this.extractObject(res?.cargo_qty));
    this.cargo_contents = this.normalizeMapping(this.extractObject(res?.cargo_contents));
    this.voyMin = this.normalizeMapping(this.extractObject(res?.voy_min));
    this.voyMax = this.normalizeMapping(this.extractObject(res?.voy_max));
    this.voyMinMeraki = this.normalizeMapping(this.extractObject(res?.voy_min_meraki));
    this.voyMaxMeraki = this.normalizeMapping(this.extractObject(res?.voy_max_meraki));
    this.dischMin = this.normalizeMapping(this.extractObject(res?.disch_min));
    this.dischMax = this.normalizeMapping(this.extractObject(res?.disch_max));
    this.cargoType = this.normalizeMapping(this.extractObject(res?.cargo_type));
    this.loadTemp = this.normalizeMapping(this.extractObject(res?.load_temp));
    this.pourPoint = this.normalizeMapping(this.extractObject(res?.pour_point));
    this.loadPorts = this.normalizeMapping(this.extractObject(res?.load_ports));
    this.dischargePorts = this.normalizeMapping(this.extractObject(res?.discharge_ports));
  }

  /** Unique Cargo Sub Type values from all tank profiles (All via multi-select). */
  private rebuildCargoSubTypeOptions(): void {
    const unique = new Set<string>();
    Object.values(this.cargo_contents || {}).forEach((value) => {
      const label = String(value ?? '').trim();
      if (label) {
        unique.add(label);
      }
    });
    this.cargoSubTypeOptions = Array.from(unique).sort((a, b) =>
      a.localeCompare(b),
    );
    this.selectedCargoSubTypes = this.selectedCargoSubTypes.filter((value) =>
      this.cargoSubTypeOptions.includes(value),
    );
  }

  onCargoSubTypesChanged(values: string[]): void {
    this.selectedCargoSubTypes = [...(values || [])];
    this.outputCalcSumCache.clear();
    this.rebuildTankFilterOptions();
    // Filters default to collapsed (temps only); expand icon shows factors.
    if (this.isTableFilterActive) {
      this.isCollapsedView = true;
    }
    this.updateVisibleColumns();
    this.clearPortHover();
    this.cdr.markForCheck();
  }

  onTanksChanged(values: string[]): void {
    this.selectedTanks = [...(values || [])];
    this.outputCalcSumCache.clear();
    if (this.isTableFilterActive) {
      this.isCollapsedView = true;
    }
    this.updateVisibleColumns();
    this.clearPortHover();
    this.cdr.markForCheck();
  }

  /** True when a concrete cargo sub type subset is selected (not empty / not All). */
  get isCargoSubTypeFiltered(): boolean {
    return this.isPartialMultiSelect(
      this.selectedCargoSubTypes,
      this.cargoSubTypeOptions,
    );
  }

  /** True when a concrete tank subset is selected (not empty / not All). */
  get isTankFiltered(): boolean {
    return this.isPartialMultiSelect(this.selectedTanks, this.tankFilterOptions);
  }

  /** Any table filter that should show the three day-wise consumption columns. */
  get isTableFilterActive(): boolean {
    return this.isCargoSubTypeFiltered || this.isTankFiltered;
  }

  private isPartialMultiSelect(selected: string[], options: string[]): boolean {
    if (!selected?.length || !options?.length) {
      return false;
    }
    return !options.every((opt) => selected.includes(opt));
  }

  /** Unique tank keys for the Tank multi-select (respects Cargo Sub Type filter). */
  private rebuildTankFilterOptions(): void {
    const tanks = new Set<string>();
    this.columns.forEach((col) => {
      const tankKey = this.getTankKeyFromColumn(col);
      if (!tankKey) {
        return;
      }
      if (this.isCargoSubTypeFiltered) {
        const key = this.normalizeKey(tankKey);
        const subType = String(this.cargo_contents?.[key] ?? '').trim();
        if (!this.selectedCargoSubTypes.includes(subType)) {
          return;
        }
      }
      tanks.add(tankKey.toUpperCase());
    });
    this.tankFilterOptions = Array.from(tanks).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );
    this.selectedTanks = this.selectedTanks.filter((tank) =>
      this.tankFilterOptions.includes(tank),
    );
  }

  extractObject(val: any): any {
    if (!val) return {};
    if (typeof val === 'object') return val;
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        if (parsed && typeof parsed === 'object') {
          const possibleKeys = [
            'cargo_qty', 'cargo_contents', 'voy_min', 'voy_max',
            'disch_min', 'disch_max', 'cargo_type', 'load_temp', 'pour_point',
            'load_ports', 'discharge_ports'
          ];
          for (let k of possibleKeys) {
            if (parsed[k]) return parsed[k];
          }
          return parsed;
        }
        return {};
      } catch {
        return {};
      }
    }
    return {};
  }

  normalizeMapping(obj: any) {
    const out: any = {};
    if (!obj || typeof obj !== 'object') return out;
    Object.keys(obj).forEach(k => {
      out[this.normalizeKey(k)] = obj[k];
    });
    return out;
  }

  normalizeKey(col: string): string {
    return String(col)
      .replace(" (°C)", "")
      .replace(/-/g, '')
      .replace(/\s+/g, '')
      .toUpperCase()
      .trim();
  }

  loadFallbackData(): void {
    this.http.get<any>('assets/vessel.json').subscribe({
      next: (data) => {
        this.columns = data.columns;
        this.indexes = data.index;
        this.rowData = this.processData(data);
        this.originalRowData = this.deepCloneRows(this.rowData);
        this.allColumns = this.columns.map(c => ({
          name: c,
          label: this.getColumnLabel(c),
          visible: true
        }));
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  processData(json: any): any[] {
    return json.data.map((row: any[]) => {
      const rowObj: any = {};
      json.columns.forEach((col: string, index: number) => {
        rowObj[col] = row[index] ?? '';
      });
      return rowObj;
    });
  }

  getColumnLabel(col: string): string {
    return col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  visibleColumns: string[] = [];
  /** Frozen weather column → position among the frozen columns. */
  frozenColumnIndex: { [col: string]: number } = {};
  frozenColumnCount = 0;

  updateVisibleColumns(): void {
    const selected = this.allColumns.filter((c) => c.visible).map((c) => c.name);
    const filtering = this.isTableFilterActive;

    // Cargo / Tank multi-select: matching tanks only; hide overall right-side calc columns.
    let columns = filtering
      ? selected.filter((col) => this.columnMatchesActiveFilters(col))
      : selected;

    // Collapsed = temperature columns only; expand reveals tank subfactors.
    if (this.isCollapsedView) {
      columns = columns.filter((col) => !this.shouldHideCollapsedColumn(col));
    }

    // Weather columns are frozen beside S.No / Date, so they must lead the scrolling columns.
    const weather = FROZEN_WEATHER_COLUMN_KEYS
      .map((key) => columns.find((col) => !this.getTankKeyFromColumn(col) && col.toLowerCase().includes(key)))
      .filter((col): col is string => !!col);
    if (!this.showWeatherColumns) {
      columns = columns.filter((col) => !weather.includes(col));
    }
    const frozen = this.showWeatherColumns ? weather : [];
    this.frozenColumnIndex = {};
    frozen.forEach((col, idx) => (this.frozenColumnIndex[col] = idx));
    this.frozenColumnCount = frozen.length;

    this.visibleColumns = [
      ...frozen,
      ...columns.filter((col) => !frozen.includes(col)),
      ...this.getSelectedOutputCalcColumnIds(),
    ];
    this.ensurePinnedTank();
  }

  getVisibleColumns(): string[] {
    return this.visibleColumns;
  }

  isFrozenColumn(col: string): boolean {
    return col in this.frozenColumnIndex;
  }

  isLastFrozenColumn(col: string): boolean {
    return this.frozenColumnIndex[col] === this.frozenColumnCount - 1;
  }

  /** Sticky `left` offset for a frozen weather column (widths come from the SCSS vars). */
  getFrozenColumnLeft(col: string): string | null {
    const idx = this.frozenColumnIndex[col];
    if (idx === undefined) return null;
    return `calc(var(--forecast-sno-width) + var(--forecast-date-width) + ${idx} * var(--forecast-weather-width))`;
  }

  getColumnDisplayName(col: string): string {
    if (col === OUTPUT_CALC_TOTAL_CONSUMPTION) {
      return 'Total Consumption';
    }
    if (col === OUTPUT_CALC_CONSUMPTION_MAINTAIN) {
      return 'Consumption (Maintain)';
    }
    if (col === OUTPUT_CALC_CONSUMPTION_RAISE) {
      return 'Consumption (Raise)';
    }
    return col;
  }

  isOutputCalcColumn(col: string): boolean {
    return String(col || '').startsWith(OUTPUT_CALC_COL_PREFIX);
  }

  getOutputCalcCellValue(rowIndex: number, col: string): string | number {
    if (!this.isOutputCalcColumn(col)) {
      return '';
    }
    const factorKeys = this.getSelectedFactorKeysForColumn(col);
    if (!factorKeys.length) {
      return '';
    }
    const sum = this.getOutputCalcGroupSum(rowIndex, col, factorKeys);
    if (sum == null) {
      return '';
    }
    return sum;
  }

  /** Sum of one static consumption column across all forecast days. */
  getOutputCalcColumnTotal(col: string): string | number {
    if (!this.isOutputCalcColumn(col) || !this.rowData?.length) {
      return '';
    }
    let sum = 0;
    let found = false;
    for (let i = 0; i < this.rowData.length; i++) {
      const cell = this.getOutputCalcCellValue(i, col);
      if (cell === '' || cell == null) {
        continue;
      }
      const num = Number(cell);
      if (!Number.isFinite(num)) {
        continue;
      }
      sum += num;
      found = true;
    }
    if (!found) {
      return 0;
    }
    return sum;
  }

  /**
   * Footer totals:
   * - Filtered view → three output-calc columns
   * - Default view → Bunker Cons - Raise / Maintain + Overall Cons only
   */
  shouldShowColumnTotal(col: string): boolean {
    if (this.isOutputCalcColumn(col)) {
      return this.isTableFilterActive;
    }
    return !this.isTableFilterActive && this.isDefaultSummaryTotalColumn(col);
  }

  getColumnFooterTotal(col: string): string | number {
    if (this.isOutputCalcColumn(col)) {
      return this.getOutputCalcColumnTotal(col);
    }
    return this.getDefaultSummaryColumnTotal(col);
  }

  private isDefaultSummaryTotalColumn(col: string): boolean {
    const normalized = String(col || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
    return (
      normalized.includes('bunker cons - raise') ||
      normalized.includes('bunker cons - maintain') ||
      normalized === 'overall cons' ||
      normalized.includes('overall cons')
    );
  }

  private getDefaultSummaryColumnTotal(col: string): string | number {
    if (!this.isDefaultSummaryTotalColumn(col) || !this.rowData?.length) {
      return '';
    }
    let sum = 0;
    let found = false;
    for (const row of this.rowData) {
      const raw = row?.[col];
      if (raw === '' || raw == null) {
        continue;
      }
      const num = Number(String(raw).replace(/,/g, '').trim());
      if (!Number.isFinite(num)) {
        continue;
      }
      sum += num;
      found = true;
    }
    if (!found) {
      return 0;
    }
    return sum;
  }

  /**
   * Three static day-wise columns when Cargo Sub Type and/or Tank is filtered:
   * Total Consumption, Consumption (Maintain), Consumption (Raise).
   */
  private getSelectedOutputCalcColumnIds(): string[] {
    if (!this.isTableFilterActive) {
      return [];
    }
    return [
      OUTPUT_CALC_TOTAL_CONSUMPTION,
      OUTPUT_CALC_CONSUMPTION_MAINTAIN,
      OUTPUT_CALC_CONSUMPTION_RAISE,
    ];
  }

  private getSelectedFactorKeysForColumn(col: string): string[] {
    if (col === OUTPUT_CALC_TOTAL_CONSUMPTION) {
      return [...TOTAL_CONSUMPTION_FACTOR_KEYS];
    }
    if (col === OUTPUT_CALC_CONSUMPTION_MAINTAIN) {
      return [...CONSUMPTION_MAINTAIN_FACTOR_KEYS];
    }
    if (col === OUTPUT_CALC_CONSUMPTION_RAISE) {
      return [...CONSUMPTION_RAISE_FACTOR_KEYS];
    }
    return [];
  }

  private getOutputCalcGroupSum(
    rowIndex: number,
    colId: string,
    factorKeys: string[],
  ): number | null {
    const selectionKey = factorKeys.slice().sort().join(',');
    const cargoKey = this.selectedCargoSubTypes.slice().sort().join('|') || 'ALL';
    const tankKey = this.selectedTanks.slice().sort().join('|') || 'ALL';
    const cacheKey = `${rowIndex}__${colId}__${cargoKey}__${tankKey}__${selectionKey}`;
    if (this.outputCalcSumCache.has(cacheKey)) {
      return this.outputCalcSumCache.get(cacheKey) ?? null;
    }

    const row = this.rowData[rowIndex];
    if (!row || !factorKeys.length) {
      this.outputCalcSumCache.set(cacheKey, null);
      return null;
    }

    let sum = 0;
    let found = false;
    for (const tank of this.getTanksForOutputCalculation()) {
      for (const factorKey of factorKeys) {
        const sourceCol = this.findTankFactorColumn(tank, factorKey);
        if (!sourceCol) {
          continue;
        }
        const raw = row[sourceCol];
        const num = Number(String(raw ?? '').replace(/,/g, '').trim());
        if (!Number.isFinite(num)) {
          continue;
        }
        sum += num;
        found = true;
      }
    }

    const result = found ? sum : null;
    this.outputCalcSumCache.set(cacheKey, result);
    return result;
  }

  /** Tanks included in day-wise sums — respects Cargo Sub Type + Tank multi-select. */
  private getTanksForOutputCalculation(): string[] {
    const tanks = new Set<string>();
    this.columns.forEach((col) => {
      const tankKey = this.getTankKeyFromColumn(col);
      if (!tankKey) {
        return;
      }
      if (!this.tankPassesActiveFilters(tankKey)) {
        return;
      }
      tanks.add(tankKey.toUpperCase());
    });
    return Array.from(tanks);
  }

  /**
   * Find the source column for a tank + factor suffix (steam, bunker_r, …).
   * Matches factor as a whole token so steam ≠ steam_m / steam_r.
   */
  private findTankFactorColumn(tankKey: string, factorKey: string): string | null {
    const tankNorm = this.normalizeKey(tankKey);
    const factor = String(factorKey || '').toLowerCase();
    if (!factor) {
      return null;
    }

    const matches = this.columns.filter((col) => {
      const colTank = this.getTankKeyFromColumn(col);
      if (!colTank || this.normalizeKey(colTank) !== tankNorm) {
        return false;
      }
      if (this.isTempColumn(col)) {
        return false;
      }
      return this.extractFactorTokens(col).includes(factor);
    });

    if (!matches.length) {
      return null;
    }

    // Prefer the shortest / most exact column name when multiple match.
    return matches.sort((a, b) => a.length - b.length)[0];
  }

  private extractFactorTokens(col: string): string[] {
    const cleaned = String(col)
      .toLowerCase()
      .replace(/\(°c\)/g, ' ')
      .replace(/\d+-port|\d+-stbd|slop-port|slop-stbd/gi, ' ')
      .replace(/[^a-z0-9_]+/g, ' ')
      .trim();
    return cleaned.split(/\s+/).filter(Boolean);
  }

  /**
   * Keep only columns that belong to tanks matching Cargo Sub Type + Tank filters.
   * Columns without a tank key (overall bunker/ae cons, boiler load, etc.) are excluded.
   */
  private columnMatchesActiveFilters(col: string): boolean {
    const tankKey = this.getTankKeyFromColumn(col);
    if (!tankKey) {
      return false;
    }
    return this.tankPassesActiveFilters(tankKey);
  }

  private tankPassesActiveFilters(tankKey: string): boolean {
    const key = this.normalizeKey(tankKey);
    if (this.isCargoSubTypeFiltered) {
      const tankSubType = String(this.cargo_contents?.[key] ?? '').trim();
      if (!this.selectedCargoSubTypes.includes(tankSubType)) {
        return false;
      }
    }
    if (this.isTankFiltered) {
      const upper = String(tankKey).toUpperCase();
      if (!this.selectedTanks.some((t) => t.toUpperCase() === upper)) {
        return false;
      }
    }
    return true;
  }

  /** Hide detailed per-tank metrics in collapsed view (steam/bunker/ae/pipeline_loss families). */
  private shouldHideCollapsedColumn(col: string): boolean {
    const normalized = col.toLowerCase();

    // Summary bunker columns stay visible in collapsed view.
    if (
      normalized.includes('bunker cons - raise') ||
      normalized.includes('bunker cons - maintain')
    ) {
      return false;
    }

    // const staticHidden = [
    //   'latitude', 'longitude', 'water temp', 'air temp',
    //   'wind speed', 'grad', 'temp_drop', 'theory',
    //   'dhe', 'a/e cons', 'ae cons',
    // ];
    const staticHidden = [
      'latitude', 'longitude', 'grad', 'temp_drop', 'theory',
      'dhe', 'a/e cons', 'ae cons',
    ];
    if (staticHidden.some(suffix => normalized.includes(suffix))) {
      return true;
    }

    const tankMetricFamilies = ['steam', 'pipeline_loss'];
    if (tankMetricFamilies.some(metric => normalized.includes(metric))) {
      return true;
    }

    // Per-tank bunker metrics (bunker / bunker_m / bunker_r) only.
    if (
      normalized.includes('bunker_m') ||
      normalized.includes('bunker_r') ||
      /(?:^|[\s_-])bunker(?:_[mr])?(?:$|[\s_-])/i.test(normalized)
    ) {
      return true;
    }

    // Per-tank AE factor (ae / ae_m / ae_r) — not the separate "ae cons" columns.
    return (
      normalized.includes('_ae') ||
      normalized.includes(' ae') ||
      normalized.includes('-ae') ||
      /(?:^|[\s_-])ae(?:_[mr])?(?:$|[\s_-])/i.test(normalized)
    );
  }

  toggleTableView() {
    this.isCollapsedView = !this.isCollapsedView;
    this.updateVisibleColumns();
    this.cdr.markForCheck();
  }

  toggleWeatherColumns() {
    this.showWeatherColumns = !this.showWeatherColumns;
    this.updateVisibleColumns();
    this.cdr.markForCheck();
  }

  /** Returns formatted second row date string for header banner */
  getSecondRowDateFormatted(): string {
    if (this.indexes && this.indexes.length > 1) {
      return this.formatDate(this.indexes[1]);
    }
    return '';
  }

  // ─── Inline editing ─────────────────────────────────────────────────────────

  /**
   * Opens an input for the given cell. Focuses it after Angular renders.
   * `initialText` (a typed digit) replaces the value, spreadsheet style.
   */
  startEdit(rowIndex: number, col: string, initialText?: string): void {
    if (rowIndex === 0) return;
    this.activeEditCell = `${rowIndex}_${col}`;
    this.editDraft = initialText ?? String(this.rowData[rowIndex]?.[col] ?? '');
    setTimeout(() => {
      const input = this.tableEl?.querySelector<HTMLInputElement>('.cell-input-active');
      if (!input) return;
      input.focus();
      if (initialText !== undefined) {
        input.value = initialText;
        input.setSelectionRange(initialText.length, initialText.length);
      } else {
        input.select();
      }
      this.scrollCellIntoView(input);
    });
  }

  /** Commits the typed value and closes the input. */
  finishEdit(rowIndex: number, col: string, rawValue: string): void {
    if (rowIndex === 0) return;
    const key = `${rowIndex}_${col}`;
    if (this.activeEditCell !== key) return;

    if (this.isBoilerLoadColumn(col)) {
      this.applyCellChange(rowIndex, col, String(rawValue ?? '').trim());
      this.activeEditCell = null;
      return;
    }

    const num = this.parseEditableCellValue(rawValue);
    if (num === null) {
      if (String(rawValue ?? '').trim()) {
        this.toastr.warning('Please enter a valid number.');
      }
      this.activeEditCell = null;
      this.cdr.markForCheck();
      return;
    }

    this.applyCellChange(rowIndex, col, num);
    this.activeEditCell = null;
  }

  private isBoilerLoadColumn(col: string): boolean {
    return col.toLowerCase().includes('boiler load');
  }

  private parseEditableCellValue(rawValue: string): number | null {
    const trimmed = String(rawValue ?? '').trim();
    if (!trimmed || !/^-?\d+(\.\d+)?$/.test(trimmed)) {
      return null;
    }

    const num = Number(trimmed);
    return Number.isFinite(num) ? num : null;
  }

  /** Cancels the inline edit without saving. */
  cancelEdit(): void {
    this.activeEditCell = null;
  }

  isActiveEdit(rowIndex: number, col: string): boolean {
    return this.activeEditCell === `${rowIndex}_${col}`;
  }

  /** Live status shown at the left of the action bar. */
  get editStatus(): { tone: 'editing' | 'pending' | 'ready' | 'idle'; text: string } {
    if (this.activeEditCell) {
      const sep = this.activeEditCell.indexOf('_');
      const row = Number(this.activeEditCell.slice(0, sep));
      const col = this.activeEditCell.slice(sep + 1).replace(/\s*\(°C\)\s*$/, '');
      return { tone: 'editing', text: `Editing ${col} · ${this.formatDate(this.indexes[row])}` };
    }
    const changed = Object.keys(this.editedCells).length;
    if (changed) {
      return {
        tone: 'pending',
        text: `${changed} ${changed === 1 ? 'value' : 'values'} changed · calibrate to update the plan`,
      };
    }
    // Calibration clears editedCells but keeps the manual-adjustment marks
    if (Object.keys(this.manuallyAdjustedCells).length) {
      return { tone: 'ready', text: 'Plan calibrated · ready to submit' };
    }
    return { tone: 'idle', text: 'No changes yet · click any temperature to start editing' };
  }

  /** Row whose cell editor is open — gets a subtle highlight. */
  isEditingRow(rowIndex: number): boolean {
    return !!this.activeEditCell?.startsWith(`${rowIndex}_`);
  }

  isEditedCell(rowIndex: number, col: string): boolean {
    return !!this.editedCells[`${rowIndex}_${col}`];
  }

  isFlashingCell(rowIndex: number, col: string): boolean {
    return this.flashCellKey === `${rowIndex}_${col}`;
  }

  /** Typed value vs the previous day, e.g. "+0.4" / "-1.2"; '' when not applicable. */
  getEditDelta(rowIndex: number, col: string): string {
    if (rowIndex < 1 || !this.isTempColumn(col)) return '';
    const draft = this.parseEditableCellValue(this.editDraft);
    const prev = this.toNumberOrNull(this.rowData[rowIndex - 1]?.[col]);
    if (draft === null || prev === null) return '';
    const diff = Math.round((draft - prev) * 100) / 100;
    return diff > 0 ? `+${diff}` : `${diff}`;
  }

  /** Same rule as the red "temp increased" highlight, applied to the typed value. */
  isEditDeltaOverTolerance(rowIndex: number, col: string): boolean {
    const delta = this.getEditDelta(rowIndex, col);
    return delta !== '' && Number(delta) > this.getActiveTolerance();
  }

  // ─── Keyboard navigation (spreadsheet style) ────────────────────────────────

  /** Keys on a focused cell that is not being edited. */
  onCellKeydown(event: KeyboardEvent, rowIndex: number, col: string): void {
    const key = event.key;
    if (event.altKey && (key === 'ArrowUp' || key === 'ArrowDown')) {
      event.preventDefault();
      this.nudgeCell(rowIndex, col, key === 'ArrowUp' ? 1 : -1);
      return;
    }
    if (key === 'Enter' || key === 'F2') {
      event.preventDefault();
      this.startEdit(rowIndex, col);
      return;
    }
    if (/^[0-9]$/.test(key) && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      this.startEdit(rowIndex, col, key);
      return;
    }

    let target: { row: number; col: string } | null = null;
    switch (key) {
      case 'ArrowUp': target = this.getNeighbourCell(rowIndex, col, -1, 0); break;
      case 'ArrowDown': target = this.getNeighbourCell(rowIndex, col, 1, 0); break;
      case 'ArrowLeft': target = this.getNeighbourCell(rowIndex, col, 0, -1); break;
      case 'ArrowRight': target = this.getNeighbourCell(rowIndex, col, 0, 1); break;
      case 'Tab':
        target = this.getNeighbourCell(rowIndex, col, 0, event.shiftKey ? -1 : 1, true);
        // Past the last/first cell, let Tab leave the table normally
        if (!target) return;
        break;
      default:
        return;
    }
    event.preventDefault();
    if (target) this.focusCellAt(target.row, target.col);
  }

  /** Keys inside the open cell editor: commit and move like a spreadsheet. */
  onEditorKeydown(event: KeyboardEvent, rowIndex: number, col: string, input: HTMLInputElement): void {
    const key = event.key;
    if (key === 'Escape') {
      event.preventDefault();
      this.cancelEdit();
      this.focusCellAt(rowIndex, col);
      return;
    }
    if (event.altKey && (key === 'ArrowUp' || key === 'ArrowDown')) {
      event.preventDefault();
      this.nudgeCell(rowIndex, col, key === 'ArrowUp' ? 1 : -1, input);
      return;
    }

    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const allSelected = start === 0 && end === input.value.length;
    let target: { row: number; col: string } | null;
    switch (key) {
      case 'Enter': target = this.getNeighbourCell(rowIndex, col, event.shiftKey ? -1 : 1, 0); break;
      case 'Tab': target = this.getNeighbourCell(rowIndex, col, 0, event.shiftKey ? -1 : 1, true); break;
      case 'ArrowUp': target = this.getNeighbourCell(rowIndex, col, -1, 0); break;
      case 'ArrowDown': target = this.getNeighbourCell(rowIndex, col, 1, 0); break;
      // Left/Right move the caret until it reaches the edge of the text
      case 'ArrowLeft':
        if (!allSelected && (start !== 0 || end !== 0)) return;
        target = this.getNeighbourCell(rowIndex, col, 0, -1);
        break;
      case 'ArrowRight':
        if (!allSelected && start !== input.value.length) return;
        target = this.getNeighbourCell(rowIndex, col, 0, 1);
        break;
      default:
        return;
    }

    event.preventDefault();
    this.finishEdit(rowIndex, col, input.value);
    if (target) {
      this.startEdit(target.row, target.col);
    } else {
      this.focusCellAt(rowIndex, col);
    }
  }

  onEditorInput(input: HTMLInputElement): void {
    this.editDraft = input.value;
  }

  /** Temperature + Boiler/Heating columns in on-screen order (skips calculated columns). */
  private getNavigableColumns(): string[] {
    return this.visibleColumns.filter(
      (c) => !this.isOutputCalcColumn(c) && (this.isTempColumn(c) || this.isEditableColumn(c)),
    );
  }

  /** Next editable cell in a direction; `wrap` continues onto the next/previous day. Day 1 is skipped. */
  private getNeighbourCell(
    rowIndex: number,
    col: string,
    dRow: number,
    dCol: number,
    wrap = false,
  ): { row: number; col: string } | null {
    const cols = this.getNavigableColumns();
    let c = cols.indexOf(col);
    let r = rowIndex + dRow;
    if (c < 0) return null;
    if (dCol) {
      c += dCol;
      if (c < 0 || c >= cols.length) {
        if (!wrap) return null;
        r += dCol;
        c = dCol > 0 ? 0 : cols.length - 1;
      }
    }
    if (r < 1 || r >= this.rowData.length) return null;
    return { row: r, col: cols[c] };
  }

  /** Focuses a cell (not editing) once Angular has rendered it. */
  private focusCellAt(rowIndex: number, col: string): void {
    const id = `${rowIndex}_${col}`;
    setTimeout(() => {
      const el = Array.from(this.tableEl?.querySelectorAll<HTMLElement>('[data-cell]') ?? [])
        .find((node) => node.dataset['cell'] === id);
      if (!el) return;
      el.focus({ preventScroll: true });
      this.scrollCellIntoView(el);
    });
  }

  /** Keeps the cell clear of the sticky S.No/Date/weather and W. AVG columns (see td scroll-margin). */
  private scrollCellIntoView(el: HTMLElement): void {
    el.closest('td')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  // ─── +/− steppers ───────────────────────────────────────────────────────────

  /**
   * Round-to-nearest-whole then step.
   * Up: 65.6 → 66  |  65.0 → 66      Down: 65.6 → 65  |  65.0 → 64
   */
  private stepValue(current: number, dir: 1 | -1): number {
    if (dir > 0) return current % 1 === 0 ? current + 1 : Math.ceil(current);
    return current % 1 === 0 ? current - 1 : Math.floor(current);
  }

  incrementCell(rowIndex: number, col: string): void {
    if (rowIndex === 0) return;
    const current = Number(this.rowData[rowIndex]?.[col]) || 0;
    this.applyCellChange(rowIndex, col, this.stepValue(current, 1));
  }

  decrementCell(rowIndex: number, col: string): void {
    if (rowIndex === 0) return;
    const current = Number(this.rowData[rowIndex]?.[col]) || 0;
    this.applyCellChange(rowIndex, col, this.stepValue(current, -1));
  }

  /** +/− button: while that cell is being edited, step from the typed value and keep the editor open. */
  onStepClick(rowIndex: number, col: string, dir: 1 | -1): void {
    const input = this.isActiveEdit(rowIndex, col)
      ? this.tableEl?.querySelector<HTMLInputElement>('.cell-input-active') ?? undefined
      : undefined;
    this.nudgeCell(rowIndex, col, dir, input);
  }

  private nudgeCell(rowIndex: number, col: string, dir: 1 | -1, input?: HTMLInputElement): void {
    if (rowIndex === 0 || !this.isTempColumn(col)) return;
    if (!input) {
      dir > 0 ? this.incrementCell(rowIndex, col) : this.decrementCell(rowIndex, col);
      return;
    }
    const typed = this.parseEditableCellValue(input.value);
    const next = this.stepValue(typed ?? (Number(this.rowData[rowIndex]?.[col]) || 0), dir);
    this.applyCellChange(rowIndex, col, next);
    input.value = String(next);
    this.editDraft = input.value;
    input.select();
  }

  private flashCell(key: string): void {
    this.flashCellKey = key;
    clearTimeout(this.flashTimer);
    this.flashTimer = setTimeout(() => {
      this.flashCellKey = null;
      this.cdr.markForCheck();
    }, 650);
  }

  trackByCol(index: number, col: string): string {
    return col;
  }

  trackByRow(index: number, row: any): any {
    return index;
  }

  // ─── Undo last change (one step at a time) ───────────────────────────────────

  canDiscardChange(): boolean {
    return this.editHistory.length > 0;
  }

  discardLastChange(): void {
    const last = this.editHistory.pop();
    if (!last) {
      this.toastr.info('No changes to discard.');
      return;
    }

    this.rowData[last.rowIndex][last.column] = last.previousValue;

    const key = `${last.rowIndex}_${last.column}`;
    const hasMoreForCell = this.editHistory.some(
      (entry) => entry.rowIndex === last.rowIndex && entry.column === last.column
    );
    if (!hasMoreForCell) {
      delete this.editedCells[key];
      if (this.isTempColumn(last.column)) {
        delete this.manuallyAdjustedCells[key];
      }
    }

    this.hasEdits = Object.keys(this.editedCells).length > 0;
    this.activeEditCell = null;
    this.outputCalcSumCache.clear();
    this.persistForecastState();
    this.cdr.markForCheck();
    this.toastr.info('Last change discarded.');
  }

  // ─── Column type helpers ─────────────────────────────────────────────────────

  isTempColumn(col: string): boolean {
    if (this.isOutputCalcColumn(col)) {
      return false;
    }
    return String(col).includes('(°C)');
  }

  /**
   * Editable but NOT a temp column: Heating Hrs and Boiler Load.
   * These use click-to-edit input without +/− steppers.
   */
  isEditableColumn(col: string): boolean {
    if (this.isOutputCalcColumn(col)) {
      return false;
    }
    const c = col.toLowerCase();
    return c.includes('boiler load') || c.includes('heating hrs');
  }

  isHeatingColumn(column: string): boolean {
    const editableColumns = ['(°C)', 'Boiler Load (%)', 'Heating Hrs'];
    return editableColumns.some(key => column.toLowerCase().includes(key.toLowerCase()));
  }

  // ─── Value change tracker ────────────────────────────────────────────────────

  onValueChange(rowIndex: number, column: string, newValue: any) {
    this.applyCellChange(rowIndex, column, Number(newValue));
  }

  private applyCellChange(
    rowIndex: number,
    column: string,
    newValue: number | string,
  ): void {
    const previousValue = this.rowData[rowIndex]?.[column];
    if (String(previousValue) === String(newValue)) return;

    this.editHistory.push({ rowIndex, column, previousValue });
    this.rowData[rowIndex][column] = newValue;

    const key = `${rowIndex}_${column}`;
    this.editedCells[key] = true;
    if (this.isTempColumn(column)) {
      this.manuallyAdjustedCells[key] = true;
    }
    this.hasEdits = true;
    this.outputCalcSumCache.clear();
    this.persistForecastState();
    this.flashCell(key);
    this.cdr.markForCheck();
  }

  private getForecastDraftKey(
    voyageId?: string | number,
    heatplanNo?: string | number,
  ): string {
    return this.commonService.getForecastDraftStorageKey(voyageId, heatplanNo);
  }

  private persistForecastState(): void {
    if (!this.columns.length || !this.rowData.length) {
      return;
    }

    const responseObj = {
      columns: this.columns,
      index: this.indexes,
      data: this.rowData.map((row) => this.columns.map((col) => row[col])),
    };

    const updated = {
      ...this.heatPlanResponse,
      response: JSON.stringify(responseObj),
      alters: [...this.persistedAlters],
      forecast_draft_meta: {
        editedCells: { ...this.editedCells },
        manuallyAdjustedCells: { ...this.manuallyAdjustedCells },
        hasEdits: this.hasEdits,
        editHistory: [...this.editHistory],
      },
    };

    this.heatPlanResponse = updated;
    this.commonService.setHeatPlanData(updated);

    const draftKey = this.getForecastDraftKey();
    if (!draftKey.endsWith('--')) {
      sessionStorage.setItem(draftKey, JSON.stringify(updated));
    }
  }

  private restoreForecastDraftIfAvailable(rawResponse: any): any {
    const voyageId = rawResponse?.voyage_id;
    const heatplanNo = rawResponse?.heatplan_no ?? rawResponse?.hp_number;
    if (!voyageId) {
      return rawResponse;
    }

    const stored = sessionStorage.getItem(
      this.getForecastDraftKey(voyageId, heatplanNo),
    );
    if (!stored) {
      return rawResponse;
    }

    try {
      return JSON.parse(stored);
    } catch {
      return rawResponse;
    }
  }

  private restoreForecastDraftMeta(): void {
    const meta = this.heatPlanResponse?.forecast_draft_meta;
    if (!meta) {
      return;
    }

    this.editedCells = { ...(meta.editedCells || {}) };
    this.manuallyAdjustedCells = { ...(meta.manuallyAdjustedCells || {}) };
    this.hasEdits = !!meta.hasEdits;
    this.editHistory = Array.isArray(meta.editHistory)
      ? [...meta.editHistory]
      : [];
  }

  private clearForecastDraft(): void {
    this.commonService.clearForecastDraftStorage(
      this.heatPlanResponse?.voyage_id,
      this.heatPlanResponse?.heatplan_no ?? this.heatPlanResponse?.hp_number,
    );
  }

  private recordCalibrateChanges(before: any[], after: any[]): void {
    const changes: Array<{ rowIndex: number; column: string; previousValue: number | string }> = [];

    for (let r = 0; r < after.length; r++) {
      for (const column of this.columns) {
        const previousValue = before[r]?.[column];
        const nextValue = after[r]?.[column];
        if (previousValue !== nextValue) {
          changes.push({ rowIndex: r, column, previousValue });
        }
      }
    }

    this.editHistory = changes;
  }

  // ─── Payload & calibrate ─────────────────────────────────────────────────────

  buildHeatPlanPayload(): any {
    const mergedMap: { [day: string]: { day: string;[tank: string]: number | string } } = {};

    for (const entry of this.persistedAlters) {
      mergedMap[entry['day']] = { ...entry };
    }

    Object.keys(this.editedCells).forEach(key => {
      const underscoreIdx = key.indexOf('_');
      const rowIndex = Number(key.substring(0, underscoreIdx));
      const column = key.substring(underscoreIdx + 1);

      const tankKey = this.getTankKeyFromColumn(column);
      if (!tankKey) return;

      const day: string = this.formatDate(this.indexes[rowIndex]);
      const newTemp: number = Number(this.rowData[rowIndex]?.[column]) || 0;

      if (!mergedMap[day]) {
        mergedMap[day] = { day };
      }
      mergedMap[day][tankKey] = newTemp;
    });

    return {
      voyage_id: this.heatPlanResponse?.voyage_id || '',
      heatplan_no: this.heatPlanResponse?.hp_number || this.heatPlanResponse?.heatplan_no || '',
      alters: Object.values(mergedMap)
    };
  }

  calibrate(): void {
    if (!this.hasEdits) return;

    this.isCalibrating = true;
    const payload = this.buildHeatPlanPayload();
    this.commonService.postCalibratePlan(payload).subscribe({
      next: (res: any) => {
        try {
          const parsed = JSON.parse(res?.response);
          const newData = this.processData(parsed);
          const beforeCalibrate = this.deepCloneRows(this.rowData);

          for (let r = 0; r < newData.length; r++) {
            for (let c of this.columns) {
              this.rowData[r][c] = newData[r][c];
            }
          }

          const userEditedTempKeys = Object.keys(this.editedCells).filter((k) => {
            const column = k.substring(k.indexOf('_') + 1);
            return this.isTempColumn(column);
          });

          this.recordCalibrateChanges(beforeCalibrate, this.rowData);

          // Update originalRowData snapshot to reflect calibrated state
          this.originalRowData = this.deepCloneRows(this.rowData);

          if (Array.isArray(res?.alters) && res.alters.length > 0) {
            this.persistedAlters = [...res.alters];
          } else {
            this.persistedAlters = payload.alters as { day: string;[tank: string]: number | string }[];
          }

          // Only user-edited forecast temps stay highlighted — not cascade recalculations
          this.manuallyAdjustedCells = {};
          this.seedManuallyAdjustedFromAlters();
          userEditedTempKeys.forEach((k) => {
            this.manuallyAdjustedCells[k] = true;
          });

          this.editedCells = {};
          this.hasEdits = false;
          this.outputCalcSumCache.clear();
          this.rebuildTankFilterOptions();
          this.persistForecastState();
          this.isCalibrating = false;
          this.cdr.markForCheck();
          this.toastr.success('Data calibrated successfully!');
        } catch {
          this.isCalibrating = false;
          this.cdr.markForCheck();
          this.toastr.error('Failed to parse calibrate response.');
        }
      },
      error: () => {
        this.isCalibrating = false;
        this.cdr.markForCheck();
        this.toastr.error('Failed to calibrate data!');
      }
    });
  }

  // ─── Hover summary ───────────────────────────────────────────────────────────

  /** Filters and table changes: fall back to the pinned tank (kept for existing callers). */
  clearPortHover() {
    this.ensurePinnedTank();
    this.restorePinnedTank();
  }

  /** Header click: pin that tank so the bar keeps showing it after the cursor leaves. */
  pinTankColumn(col: string): void {
    const key = this.getColumnTankKey(col);
    if (!key) {
      return;
    }
    this.pinnedTankKey = key;
    this.showTankSummary(key, false);
  }

  restorePinnedTank(): void {
    if (this.pinnedTankKey) {
      this.showTankSummary(this.pinnedTankKey, false);
    } else if (this.hoverSummary.active) {
      this.hoverSummary.active = false;
      this.hoverSummary.currentTankKey = '';
      this.isTankPreview = false;
      this.cdr.markForCheck();
    }
  }

  /** Pinned or hovered tank: its columns get a highlight so the bar and the table line up. */
  isActiveTankColumn(col: string): boolean {
    const key = this.getColumnTankKey(col);
    return !!key && key === this.hoverSummary.currentTankKey;
  }

  isPinnedTankColumn(col: string): boolean {
    const key = this.getColumnTankKey(col);
    return !!key && key === this.pinnedTankKey;
  }

  getColumnTankKey(col: string): string | null {
    if (!this.columnTankKeyCache.has(col)) {
      const tankKey = this.isOutputCalcColumn(col) ? null : this.getTankKeyFromColumn(col);
      this.columnTankKeyCache.set(col, tankKey ? this.normalizeKey(tankKey) : null);
    }
    return this.columnTankKeyCache.get(col) ?? null;
  }

  /** Runs outside Angular (see forecastTableContainer). */
  private handleTableHover(event: MouseEvent): void {
    const cell = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-col]');
    const col = cell?.getAttribute('data-col');
    if (!col) {
      return;
    }
    const key = this.getColumnTankKey(col);
    if (!key || key === this.hoverSummary.currentTankKey) {
      return;
    }
    this.ngZone.run(() => this.showTankSummary(key, key !== this.pinnedTankKey));
  }

  /** Pins the first visible tank when nothing is pinned or the pinned tank is filtered out. */
  private ensurePinnedTank(): void {
    const visibleKeys = this.visibleColumns
      .map((col) => this.getColumnTankKey(col))
      .filter((key): key is string => !!key);
    if (!visibleKeys.length) {
      this.pinnedTankKey = '';
      return;
    }
    if (!this.pinnedTankKey || !visibleKeys.includes(this.pinnedTankKey)) {
      this.pinnedTankKey = visibleKeys[0];
      if (!this.isTankPreview) {
        this.showTankSummary(this.pinnedTankKey, false);
      }
    }
  }

  private showTankSummary(key: string, isPreview: boolean): void {
    this.isTankPreview = isPreview;
    this.hoverSummary = {
      active: true,
      currentTankKey: key,
      cargoType:
        this.heatPlanResponse?.cargo_type_name ||
        this.heatPlanResponse?.cargo_type ||
        '-',
      cargoSubType: this.cargo_contents?.[key] || '-',
      cargoQty: this.toNumberOrNull(this.cargoQty?.[key]),
      voyMin: this.toNumberOrNull(this.voyMin?.[key]),
      voyMax: this.toNumberOrNull(this.voyMax?.[key]),
      voyMinMeraki: this.toNumberOrNull(this.voyMinMeraki?.[key]),
      voyMaxMeraki: this.toNumberOrNull(this.voyMaxMeraki?.[key]),
      dischMin: this.toNumberOrNull(this.dischMin?.[key]),
      dischMax: this.toNumberOrNull(this.dischMax?.[key]),
      loadTemp: this.toNumberOrNull(this.loadTemp?.[key]),
      pourPoint: this.toNumberOrNull(this.pourPoint?.[key]),
      loadPort: this.formatPorts(this.loadPorts?.[key]),
      dischargePort: this.formatPorts(this.dischargePorts?.[key]),
    };
    this.cdr.markForCheck();
  }

  /** Tank label for the particulars bar, e.g. "1-PORT" (keys are stored as "1PORT"). */
  get activeTankLabel(): string {
    const key = this.hoverSummary.currentTankKey;
    return key.replace(/^(\d+|SLOP)(PORT|STBD|CENTER)$/i, '$1-$2') || key;
  }

  /** "32 °C – 35 °C"; "—" when the range is missing or not set (0 – 0). */
  formatRange(min: number | null, max: number | null): string {
    if (!min && !max) return '—';
    return `${min ?? '—'} °C – ${max ?? '—'} °C`;
  }

  /** "BARCELONA/PATRAS" → "BARCELONA / PATRAS"; "—" when not provided. */
  formatPorts(value: unknown): string {
    const ports = String(value ?? '')
      .split('/')
      .map((port) => port.trim())
      .filter(Boolean);
    return ports.length ? ports.join(' / ') : '—';
  }

  getTankKeyFromColumn(col: string): string | null {
    const match = String(col).match(/((?:\d+|SLOP)-(?:PORT|STBD|CENTER))/i);
    return match ? match[1].toUpperCase() : null;
  }

  // ─── Weighted Average Temperature Calculations ─────────────────────────────────

  calculateWeightedAvg(rowIndex: number): number | null {
    const row = this.rowData[rowIndex];
    if (!row) return null;

    let weightedSum = 0;
    let totalQty = 0;

    for (const col of this.columns) {
      if (!this.isTempColumn(col)) continue;
      const tankKey = this.getTankKeyFromColumn(col);
      if (!tankKey) continue;

      const rawTemp = row[col];
      if (rawTemp == null || rawTemp === '') continue;
      const temp = Number(rawTemp);
      if (!Number.isFinite(temp)) continue;

      const normKey = this.normalizeKey(tankKey);
      const rawQty = this.cargoQty?.[normKey] ?? this.cargoQty?.[tankKey];
      if (rawQty == null || rawQty === '') continue;
      const qty = Number(rawQty);
      if (!Number.isFinite(qty) || qty <= 0) continue;

      weightedSum += temp * qty;
      totalQty += qty;
    }

    if (totalQty <= 0) return null;
    return weightedSum / totalQty;
  }

  formatWeightedAvg(rowIndex: number): string {
    const val = this.calculateWeightedAvg(rowIndex);
    if (val == null || !Number.isFinite(val)) return '—';
    return val.toFixed(1);
  }

  weightedAvgTempClass(rowIndex: number): string {
    const value = this.calculateWeightedAvg(rowIndex);
    if (value == null) return '';
    return 'w-avg-val-normal';
  }

  toNumberOrNull(val: any): number | null {
    const n = Number(val);
    return isNaN(n) ? null : n;
  }

  // ─── Display helpers ─────────────────────────────────────────────────────────

  /** True when this forecast temperature was manually changed by the user. */
  isManuallyAdjustedTemp(rowIndex: number, column: string): boolean {
    if (!this.isTempColumn(column)) return false;
    const key = `${rowIndex}_${column}`;
    return !!this.manuallyAdjustedCells[key];
  }

  /** Returns current active numeric tolerance value */
  getActiveTolerance(): number {
    if (this.selectedTolerancePreset === 'custom') {
      const val = Number(this.customToleranceValue);
      return isNaN(val) || val < 0 ? 0.5 : val;
    }
    return Number(this.selectedTolerancePreset);
  }

  setTolerancePreset(preset: number | 'custom'): void {
    this.selectedTolerancePreset = preset;
    this.rowData = [...this.rowData];
    this.cdr.markForCheck();
  }

  onCustomToleranceInput(val: any): void {
    const num = Number(val);
    this.customToleranceValue = isNaN(num) || num < 0 ? 0 : num;
    this.rowData = [...this.rowData];
    this.cdr.markForCheck();
  }

  /** True when tank temperature is > tolerance higher than previous day for the same tank. */
  isTempIncreasedOverPreviousDay(rowIndex: number, column: string): boolean {
    if (rowIndex <= 0 || !this.isTempColumn(column)) return false;

    const currentVal = this.toNumberOrNull(this.rowData?.[rowIndex]?.[column]);
    const prevVal = this.toNumberOrNull(this.rowData?.[rowIndex - 1]?.[column]);

    if (currentVal === null || prevVal === null) return false;
    const tolerance = this.getActiveTolerance();
    const diff = Math.round((currentVal - prevVal) * 100) / 100;
    return diff > tolerance;
  }

  formatDate(timestamp: number): string {
    return new Date(timestamp).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  getTooltipContent(col: string): string {
    if (this.isOutputCalcColumn(col)) {
      const factors = this.getSelectedFactorKeysForColumn(col).join(', ');
      const tanks = this.getTanksForOutputCalculation();
      const scope =
        tanks.length === 1
          ? `tank ${tanks[0]}`
          : `${tanks.length || 0} selected tank(s)`;
      return factors
        ? `Day-wise sum of ${factors} across ${scope}`
        : `Day-wise output calculation across ${scope}`;
    }

    const tankKey = this.getTankKeyFromColumn(col);
    if (!tankKey) return '';

    const key = this.normalizeKey(tankKey);
    const parts: string[] = [];

    if (this.heatPlanResponse?.cargo_type)
      parts.push(`Cargo Type: ${this.heatPlanResponse.cargo_type}`);
    if (this.cargo_contents[key] !== undefined)
      parts.push(`Cargo Sub Type: ${this.cargo_contents[key]}`);
    if (this.cargoQty[key] !== undefined)
      parts.push(`Cargo Qty: ${this.cargoQty[key]}`);
    if (this.loadTemp[key] !== undefined)
      parts.push(`Load Temp: ${this.loadTemp[key]}°C`);
    if (this.pourPoint[key] !== undefined)
      parts.push(`Pour Point: ${this.pourPoint[key]}°C`);
    if (this.voyMin[key] !== undefined && this.voyMax[key] !== undefined)
      parts.push(`Voy Min: ${this.voyMin[key]}°C | Voy Max: ${this.voyMax[key]}°C`);
    if (this.dischMin[key] !== undefined && this.dischMax[key] !== undefined)
      parts.push(`Disch Min: ${this.dischMin[key]}°C | Disch Max: ${this.dischMax[key]}°C`);
    if (this.loadPorts[key])
      parts.push(`Load Port: ${this.formatPorts(this.loadPorts[key])}`);
    if (this.dischargePorts[key])
      parts.push(`Discharge Port: ${this.formatPorts(this.dischargePorts[key])}`);

    return parts.join('\n');
  }

  // ─── Navigation & actions ─────────────────────────────────────────────────────

  @HostBinding('class.ch-light') get chLight(): boolean {
    return !this.isDarkMode();
  }

  isDarkMode() {
    return this.requestFormService.isDarkMode();
  }

  goBack() {
    this.persistForecastState();

    const heatplanNo =
      this.heatPlanResponse?.heatplan_no ??
      this.heatPlanResponse?.hp_number ??
      '';
    const voyageId = this.heatPlanResponse?.voyage_id ?? '';

    this.router.navigate(['/ch-requestForm/heat-plan'], {
      queryParams: {
        fromForecast: '1',
        step: heatplanNo || undefined,
        voyage_id: voyageId || undefined,
      },
    });
  }

  goToReports() {
    this.router.navigate(['/ch-reportTable']);
  }

  buildSubmitPayload(): any {
    const data = this.rowData.map(row => {
      return this.columns.map(col => row[col]);
    });

    const responseObj = {
      columns: this.columns,
      index: this.indexes,
      data: data
    };

    return {
      voyage_id: this.heatPlanResponse?.voyage_id || '',
      heatplan_no: this.heatPlanResponse?.hp_number || this.heatPlanResponse?.heatplan_no || '',
      response: JSON.stringify(responseObj)
    };
  }

  submitHeatPlan() {
    this.isSubmitting = true;
    const payload = this.buildSubmitPayload();
    this.commonService.postSubmitHeatPlan(payload).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.isSubmitted = true;
        this.clearForecastDraft();
      },
      error: () => {
        this.isSubmitting = false;
        this.toastr.error('Something went wrong while submitting');
      }
    });
  }

  resetHeatPlan() {
    const payload = this.buildHeatPlanPayload();
    this.commonService.postResetHeatPlan(payload).subscribe({
      next: (res: any) => {
        try {
          const parsed = JSON.parse(res?.response);
          this.columns = parsed.columns;
          this.indexes = parsed.index;
          this.rowData = this.processData(parsed);
          this.originalRowData = this.deepCloneRows(this.rowData);
          this.editedCells = {};
          this.manuallyAdjustedCells = {};
          this.editHistory = [];
          this.hasEdits = false;
          this.toastr.success('Heat plan reset and updated successfully.');
        } catch {
          this.toastr.error('Work In Progress');
        }
      },
      error: () => {
        this.toastr.error('Reset API call failed.');
      }
    });
  }

  /** Download the full visible table as a CSV file */
  downloadCSV(): void {
    const visibleCols = this.getVisibleColumns();
    const headers = [
      'S.No',
      'Date',
      ...visibleCols.map((col) => this.getColumnDisplayName(col)),
    ];

    const rows = this.rowData.map((row, i) => {
      const dateStr = this.formatDate(this.indexes[i]);
      const cells = visibleCols.map((col) => {
        const val = this.isOutputCalcColumn(col)
          ? this.getOutputCalcCellValue(i, col)
          : row[col];
        const str = val === null || val === undefined ? '' : String(val);
        return str.includes(',') || str.includes('"')
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      });
      return [String(i + 1), dateStr, ...cells].join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `heatplan_${this.vesselName || 'vessel'}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  }
}
